import React, { useEffect, useState } from 'react';

// BossScreen — classic Larry Laffer-era "boss key" easter egg.
//
// Toggles a full-viewport spreadsheet overlay on Ctrl+B. Esc also closes.
// Mounted once at the App root; absolutely-positioned so it covers every
// route. Keyboard listener ignores text-input targets so typing a 'b' in
// a form field doesn't fire the shortcut.

type Row = { region: string; q1: number; q2: number; q3: number; target: number };

const ROWS: Row[] = [
  { region: 'EMEA — DACH',          q1: 1_284_300, q2: 1_412_100, q3: 1_559_820, target: 1_500_000 },
  { region: 'EMEA — Nordics',       q1:   742_880, q2:   801_440, q3:   874_210, target:   850_000 },
  { region: 'EMEA — UK & Ireland',  q1:   918_552, q2:   964_112, q3: 1_025_600, target: 1_000_000 },
  { region: 'EMEA — France',        q1:   612_440, q2:   648_922, q3:   699_104, target:   720_000 },
  { region: 'EMEA — Benelux',       q1:   489_210, q2:   521_344, q3:   567_880, target:   580_000 },
  { region: 'Americas — NAM East',  q1: 2_104_588, q2: 2_298_440, q3: 2_507_112, target: 2_400_000 },
  { region: 'Americas — NAM West',  q1: 1_899_422, q2: 2_014_900, q3: 2_201_880, target: 2_100_000 },
  { region: 'Americas — LATAM',     q1:   412_880, q2:   461_200, q3:   518_344, target:   500_000 },
  { region: 'APAC — Japan',         q1:   824_900, q2:   871_244, q3:   932_112, target:   900_000 },
  { region: 'APAC — ANZ',           q1:   491_220, q2:   528_112, q3:   577_440, target:   560_000 },
  { region: 'APAC — SEA',           q1:   388_440, q2:   421_980, q3:   478_210, target:   460_000 },
  { region: 'APAC — India',         q1:   312_880, q2:   364_440, q3:   428_112, target:   400_000 },
  { region: 'Other / Unallocated',  q1:    88_440, q2:    91_200, q3:    94_880, target:   100_000 },
];

const MONTHLY: number[] = [
  812_000, 894_000, 1_051_000, 1_142_000, 1_288_000, 1_401_000,
  1_462_000, 1_588_000, 1_714_000, 1_822_000, 1_904_000, 2_018_000,
];
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pct = (actual: number, target: number) => {
  const v = ((actual - target) / target) * 100;
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

const COLUMNS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T'];
const COL_WIDTHS = [220, 110, 110, 110, 110, 110, 110, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100];

const BossScreen: React.FC = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ctrl+B / Cmd+B toggles — ignore when typing in form fields.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'b') {
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (open && e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const totals = ROWS.reduce(
    (acc, r) => ({ q1: acc.q1 + r.q1, q2: acc.q2 + r.q2, q3: acc.q3 + r.q3, target: acc.target + r.target }),
    { q1: 0, q2: 0, q3: 0, target: 0 }
  );

  const PIE_SLICES = [
    { label: 'EMEA',     value: ROWS.slice(0, 5).reduce((s, r) => s + r.q3, 0), color: '#4472C4' },
    { label: 'Americas', value: ROWS.slice(5, 8).reduce((s, r) => s + r.q3, 0), color: '#ED7D31' },
    { label: 'APAC',     value: ROWS.slice(8, 12).reduce((s, r) => s + r.q3, 0), color: '#A5A5A5' },
    { label: 'Other',    value: ROWS[12].q3,                                      color: '#FFC000' },
  ];
  const pieTotal = PIE_SLICES.reduce((s, p) => s + p.value, 0);
  let pieAngle = -Math.PI / 2;
  const piePath = (value: number) => {
    const slice = (value / pieTotal) * 2 * Math.PI;
    const x1 = 60 + 55 * Math.cos(pieAngle);
    const y1 = 60 + 55 * Math.sin(pieAngle);
    pieAngle += slice;
    const x2 = 60 + 55 * Math.cos(pieAngle);
    const y2 = 60 + 55 * Math.sin(pieAngle);
    const large = slice > Math.PI ? 1 : 0;
    return `M60,60 L${x1.toFixed(2)},${y1.toFixed(2)} A55,55 0 ${large} 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`;
  };

  const maxMonthly = Math.max(...MONTHLY);
  const linePoints = MONTHLY.map((v, i) => {
    const x = 20 + (i * (360 / (MONTHLY.length - 1)));
    const y = 110 - (v / maxMonthly) * 90;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  // Fonts and colours chosen to match the generic "Office spreadsheet" vibe —
  // Calibri-stack, muted-blue selected row, 2016-era ribbon beige.
  return (
    <div
      className="fixed inset-0 z-[9999] bg-white"
      style={{ fontFamily: 'Calibri, "Segoe UI", "Helvetica Neue", Arial, sans-serif', fontSize: 11, color: '#222', cursor: 'default' }}
    >
      {/* Title bar */}
      <div style={{ height: 24, background: '#2b579a', color: '#fff', display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: 12 }}>
        <span style={{ fontWeight: 600 }}>FY26_Q3_Pipeline_Review_v14_FINAL_FINAL.xlsx</span>
        <span style={{ margin: '0 10px', opacity: 0.7 }}>—</span>
        <span style={{ opacity: 0.85 }}>Excel</span>
        <span style={{ flex: 1 }} />
        <span style={{ opacity: 0.7, marginRight: 10 }}>Sign in</span>
        <span style={{ display: 'inline-block', width: 44, textAlign: 'center' }}>_</span>
        <span style={{ display: 'inline-block', width: 44, textAlign: 'center' }}>▢</span>
        <span style={{ display: 'inline-block', width: 44, textAlign: 'center' }}>×</span>
      </div>
      {/* Menu bar */}
      <div style={{ height: 26, background: '#f3f2f1', borderBottom: '1px solid #d9d9d9', display: 'flex', alignItems: 'center', padding: '0 10px', gap: 14, fontSize: 12 }}>
        {['File','Home','Insert','Draw','Page Layout','Formulas','Data','Review','View','Help'].map((m, i) => (
          <span key={m} style={{ color: i === 1 ? '#2b579a' : '#555', fontWeight: i === 1 ? 600 : 400 }}>{m}</span>
        ))}
      </div>
      {/* Formula bar */}
      <div style={{ height: 24, background: '#fff', borderBottom: '1px solid #d9d9d9', display: 'flex', alignItems: 'center', padding: '0 6px', gap: 6 }}>
        <div style={{ width: 70, borderRight: '1px solid #d9d9d9', fontSize: 11, color: '#333', fontFamily: 'Consolas, monospace' }}>B17</div>
        <span style={{ color: '#999' }}>fx</span>
        <span style={{ color: '#333', fontFamily: 'Consolas, monospace' }}>=SUMIFS(Pipeline!$F:$F, Pipeline!$C:$C, A17, Pipeline!$D:$D, "&gt;="&amp;DATE(2026,7,1))</span>
      </div>

      {/* Column header row */}
      <div style={{ display: 'flex', background: '#f3f2f1', borderBottom: '1px solid #d4d2d0', height: 20 }}>
        <div style={{ width: 36, borderRight: '1px solid #d4d2d0' }} />
        {COLUMNS.map((c, i) => (
          <div key={c} style={{ width: COL_WIDTHS[i] ?? 100, textAlign: 'center', fontSize: 11, lineHeight: '20px', borderRight: '1px solid #d4d2d0', color: '#555' }}>
            {c}
          </div>
        ))}
      </div>

      {/* Sheet body */}
      <div style={{ background: '#fff', overflow: 'hidden', height: 'calc(100vh - 24px - 26px - 24px - 20px - 22px - 20px)' }}>
        {/* Title row */}
        <SheetRow num={1}>
          <SheetCell w={COL_WIDTHS[0]} bold>FY26 Q3 Pipeline Review — Confidential</SheetCell>
        </SheetRow>
        <SheetRow num={2}>
          <SheetCell w={COL_WIDTHS[0]} muted>Prepared by: Finance / Rev Ops</SheetCell>
          <SheetCell w={COL_WIDTHS[1]} muted>Date:</SheetCell>
          <SheetCell w={COL_WIDTHS[2]}>2026-10-22</SheetCell>
          <SheetCell w={COL_WIDTHS[3]} muted>Rev:</SheetCell>
          <SheetCell w={COL_WIDTHS[4]}>v14</SheetCell>
        </SheetRow>
        <SheetRow num={3} />
        {/* Header row */}
        <SheetRow num={4} selected>
          <SheetCell w={COL_WIDTHS[0]} bold>Region</SheetCell>
          <SheetCell w={COL_WIDTHS[1]} bold right>Q1</SheetCell>
          <SheetCell w={COL_WIDTHS[2]} bold right>Q2</SheetCell>
          <SheetCell w={COL_WIDTHS[3]} bold right>Q3</SheetCell>
          <SheetCell w={COL_WIDTHS[4]} bold right>Target</SheetCell>
          <SheetCell w={COL_WIDTHS[5]} bold right>Variance</SheetCell>
          <SheetCell w={COL_WIDTHS[6]} bold right>YoY</SheetCell>
        </SheetRow>
        {ROWS.map((r, i) => {
          const variance = r.q3 - r.target;
          return (
            <SheetRow num={5 + i}>
              <SheetCell w={COL_WIDTHS[0]}>{r.region}</SheetCell>
              <SheetCell w={COL_WIDTHS[1]} right>{fmt(r.q1)}</SheetCell>
              <SheetCell w={COL_WIDTHS[2]} right>{fmt(r.q2)}</SheetCell>
              <SheetCell w={COL_WIDTHS[3]} right bold>{fmt(r.q3)}</SheetCell>
              <SheetCell w={COL_WIDTHS[4]} right>{fmt(r.target)}</SheetCell>
              <SheetCell w={COL_WIDTHS[5]} right color={variance >= 0 ? '#107c10' : '#c42b1c'}>{(variance >= 0 ? '+' : '') + fmt(variance)}</SheetCell>
              <SheetCell w={COL_WIDTHS[6]} right color={variance >= 0 ? '#107c10' : '#c42b1c'}>{pct(r.q3, r.target)}</SheetCell>
            </SheetRow>
          );
        })}
        <SheetRow num={5 + ROWS.length} top>
          <SheetCell w={COL_WIDTHS[0]} bold>Total</SheetCell>
          <SheetCell w={COL_WIDTHS[1]} right bold>{fmt(totals.q1)}</SheetCell>
          <SheetCell w={COL_WIDTHS[2]} right bold>{fmt(totals.q2)}</SheetCell>
          <SheetCell w={COL_WIDTHS[3]} right bold>{fmt(totals.q3)}</SheetCell>
          <SheetCell w={COL_WIDTHS[4]} right bold>{fmt(totals.target)}</SheetCell>
          <SheetCell w={COL_WIDTHS[5]} right bold color={totals.q3 - totals.target >= 0 ? '#107c10' : '#c42b1c'}>
            {(totals.q3 - totals.target >= 0 ? '+' : '') + fmt(totals.q3 - totals.target)}
          </SheetCell>
          <SheetCell w={COL_WIDTHS[6]} right bold color={totals.q3 - totals.target >= 0 ? '#107c10' : '#c42b1c'}>{pct(totals.q3, totals.target)}</SheetCell>
        </SheetRow>
        <SheetRow num={6 + ROWS.length} />
        {/* Charts row — bar + pie + line, each a small embedded SVG */}
        <div style={{ display: 'flex', gap: 24, padding: '12px 36px 12px 48px', borderTop: '1px solid #e1dfdd' }}>
          <ChartFrame title="Monthly Revenue (YTD 2026)" width={400}>
            <svg viewBox="0 0 380 140" width="380" height="140">
              {/* y-axis */}
              <line x1="30" y1="10" x2="30" y2="120" stroke="#bbb" strokeWidth="0.5" />
              <line x1="30" y1="120" x2="370" y2="120" stroke="#bbb" strokeWidth="0.5" />
              {MONTHLY.map((v, i) => {
                const h = (v / maxMonthly) * 100;
                const x = 36 + i * 27;
                return (
                  <g key={i}>
                    <rect x={x} y={120 - h} width={20} height={h} fill="#4472C4" />
                    <text x={x + 10} y={132} textAnchor="middle" fontSize="8" fill="#555">{MONTH_LABELS[i]}</text>
                  </g>
                );
              })}
              {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                <text key={t} x={26} y={120 - t * 100 + 3} textAnchor="end" fontSize="8" fill="#777">
                  {(maxMonthly * t / 1_000_000).toFixed(1)}M
                </text>
              ))}
            </svg>
          </ChartFrame>

          <ChartFrame title="Q3 Revenue by Region" width={220}>
            <svg viewBox="0 0 220 140" width="220" height="140">
              {PIE_SLICES.map((s, i) => (
                <path key={i} d={piePath(s.value)} fill={s.color} stroke="#fff" strokeWidth="0.8" />
              ))}
              {PIE_SLICES.map((s, i) => (
                <g key={`leg${i}`} transform={`translate(130, ${22 + i * 22})`}>
                  <rect width="10" height="10" fill={s.color} />
                  <text x="14" y="9" fontSize="10" fill="#333">{s.label}</text>
                  <text x="14" y="20" fontSize="9" fill="#777">
                    {((s.value / pieTotal) * 100).toFixed(1)}%
                  </text>
                </g>
              ))}
            </svg>
          </ChartFrame>

          <ChartFrame title="YTD Trend (cumulative)" width={400}>
            <svg viewBox="0 0 400 140" width="400" height="140">
              <line x1="20" y1="10" x2="20" y2="120" stroke="#bbb" strokeWidth="0.5" />
              <line x1="20" y1="120" x2="390" y2="120" stroke="#bbb" strokeWidth="0.5" />
              <polyline points={linePoints} fill="none" stroke="#ED7D31" strokeWidth="2" />
              {MONTHLY.map((_, i) => {
                const pt = linePoints.split(' ')[i].split(',');
                return <circle key={i} cx={pt[0]} cy={pt[1]} r="2.5" fill="#ED7D31" />;
              })}
              {MONTH_LABELS.map((lbl, i) => (
                <text key={lbl} x={20 + i * (360 / (MONTHLY.length - 1))} y="132" textAnchor="middle" fontSize="8" fill="#555">{lbl}</text>
              ))}
            </svg>
          </ChartFrame>
        </div>
      </div>

      {/* Sheet tabs */}
      <div style={{ height: 22, background: '#f3f2f1', borderTop: '1px solid #d4d2d0', display: 'flex', alignItems: 'center', padding: '0 8px', gap: 2, fontSize: 11 }}>
        <SheetTab active>Summary</SheetTab>
        <SheetTab>Pipeline</SheetTab>
        <SheetTab>Forecast</SheetTab>
        <SheetTab>Attainment</SheetTab>
        <SheetTab>Scratch</SheetTab>
        <SheetTab>Pivot</SheetTab>
        <span style={{ marginLeft: 'auto', color: '#777' }}>+</span>
      </div>
      {/* Status bar */}
      <div style={{ height: 20, background: '#2b579a', color: '#fff', display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: 10, gap: 20 }}>
        <span>Ready</span>
        <span>Count: {ROWS.length}</span>
        <span>Sum: {fmt(totals.q3)}</span>
        <span style={{ marginLeft: 'auto', opacity: 0.75 }}>CONFIDENTIAL — INTERNAL USE ONLY</span>
        <span>100%</span>
      </div>
    </div>
  );
};

const SheetRow: React.FC<{ num: number; selected?: boolean; top?: boolean; children?: React.ReactNode }> = ({ num, selected, top, children }) => (
  <div style={{ display: 'flex', background: selected ? '#deebf7' : '#fff', borderTop: top ? '2px solid #333' : undefined }}>
    <div style={{ width: 36, textAlign: 'center', background: '#f3f2f1', borderRight: '1px solid #d4d2d0', borderBottom: '1px solid #e1dfdd', fontSize: 11, lineHeight: '20px', color: '#555' }}>{num}</div>
    {children}
  </div>
);

const SheetCell: React.FC<{ w: number; bold?: boolean; muted?: boolean; right?: boolean; color?: string; children?: React.ReactNode }> = ({ w, bold, muted, right, color, children }) => (
  <div style={{
    width: w,
    borderRight: '1px solid #e1dfdd',
    borderBottom: '1px solid #e1dfdd',
    padding: '0 6px',
    lineHeight: '20px',
    fontWeight: bold ? 600 : 400,
    color: color ?? (muted ? '#888' : '#222'),
    textAlign: right ? 'right' : 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }}>
    {children}
  </div>
);

const ChartFrame: React.FC<{ title: string; width: number; children: React.ReactNode }> = ({ title, width, children }) => (
  <div style={{ border: '1px solid #d4d2d0', borderRadius: 2, background: '#fff', width, padding: 6 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#333', marginBottom: 4 }}>{title}</div>
    {children}
  </div>
);

const SheetTab: React.FC<{ active?: boolean; children: React.ReactNode }> = ({ active, children }) => (
  <div style={{
    padding: '2px 10px',
    background: active ? '#fff' : 'transparent',
    border: active ? '1px solid #d4d2d0' : '1px solid transparent',
    borderBottom: active ? '1px solid #fff' : undefined,
    marginBottom: active ? -1 : 0,
    fontWeight: active ? 600 : 400,
    color: active ? '#2b579a' : '#555',
  }}>
    {children}
  </div>
);

export default BossScreen;
